import { FC, useMemo, useState, useEffect, useRef } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Topbar, Table, utils } from "@vilocnv/allsetra-core";
import { NoDataIcon } from "assets/icons";

// Data
import { useTranslation } from "react-i18next";
import { FormikHelpers } from "formik";
import {
  useAppDispatch,
  useAppSelector,
  useDispatchOnMount,
  useCurrentUser
} from "hooks";
import {
  selectAllRoles,
  selectDrawerSelectedAccountId,
  selectMileageDeviationState
} from "app/data/selectors";
import { resetGooglePredictatedPlaces, getAllRolesThunk } from "app/features";
import { ALL_MILEAGE_DEVIATIONS_TABLE_COLUMNS } from "app/data/constants/mileagedeviationsConstants";
import { getAllMileageDeviationsThunk } from "app/features/mileageDeviations/mileageDeviationsSlice";
import MileageDeviationCommentCard from "components/cards/MileageDeviations/MileageDeviationCommentCard/MileageDeviationCommentCard";
import MileageDeviationFilterModal, {
  IMileageDeviationFilter
} from "components/modals/MileageDeviationFilterModal";

const MileageDeviations: FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { permissions, isDriver } = useCurrentUser();
  const [searchParams, setSearchParams] = useSearchParams();

  // Local States
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [sortAscending, setSortAscending] = useState(false);
  const [activeFilters, setActiveFilters] = useState<IMileageDeviationFilter>({
    userUniqueId: "",
    objectUniqueId: ""
  });

  const isInitialMount = useRef(true);
  const previousPayloadRef = useRef<string>("");

  // Global States
  const drawerSelectedAccountId =
    useAppSelector(selectDrawerSelectedAccountId) || "";
  const roles = useAppSelector(selectAllRoles);

  const { mileageDeviationLoading, mileageDeviations, totalMileageDeviations } =
    useAppSelector(selectMileageDeviationState);

  useEffect(() => {
    if (
      isDriver ||
      (!permissions.canEditMileageDeviationStatus &&
        !permissions.canEditMileageDeviationComments)
    ) {
      navigate("/dashboard", { replace: true });
    }
  }, [isDriver, permissions, navigate]);

  const { i18n } = useTranslation([
    "translation",
    "tableHeadingsTranslation",
    "formFieldsTranslation"
  ]);

  // Custom pagination handler that maintains filters
  useEffect(() => {
    if (!drawerSelectedAccountId) return;

    const page = parseInt(searchParams.get("page") || "1", 10);
    const itemsPerPage = parseInt(searchParams.get("itemsPerPage") || "10", 10);
    const search = searchParams.get("search") || "";

    const whereConditions: any[] = [];

    if (activeFilters.userUniqueId) {
      whereConditions.push({
        field: "User.UniqueId",
        value: activeFilters.userUniqueId,
        type: 0
      });
    }

    if (activeFilters.objectUniqueId) {
      whereConditions.push({
        field: "Object.UniqueId",
        value: activeFilters.objectUniqueId,
        type: 0
      });
    }

    if (search) {
      whereConditions.push({
        field: "user.email",
        value: search,
        type: 1
      });
    }

    const payload = {
      params: {
        page,
        itemsPerPage,
        where: whereConditions,
        orderBy: [{ field: "recordedDate", ascending: sortAscending }]
      },
      accountId: drawerSelectedAccountId
    };

    const payloadKey = JSON.stringify({
      page,
      itemsPerPage,
      where: whereConditions,
      orderBy: payload.params.orderBy,
      accountId: drawerSelectedAccountId
    });

    if (isInitialMount.current || payloadKey !== previousPayloadRef.current) {
      dispatch(getAllMileageDeviationsThunk(payload));
      previousPayloadRef.current = payloadKey;

      if (isInitialMount.current) {
        isInitialMount.current = false;
      }
    }
  }, [
    searchParams,
    drawerSelectedAccountId,
    activeFilters.userUniqueId,
    activeFilters.objectUniqueId,
    sortAscending,
    dispatch
  ]);

  useDispatchOnMount(getAllRolesThunk, roles.length ? undefined : true);

  const tableColumns = useMemo(
    () => ALL_MILEAGE_DEVIATIONS_TABLE_COLUMNS([], t),
    [i18n.language]
  );

  const handleSortByDate = () => {
    setSortAscending(!sortAscending);
  };

  const handleChangeRowsPerPage = (newPerPage: number) => {
    const currentSearch = searchParams.get("search") || "";
    const params: Record<string, string> = {
      page: "1",
      itemsPerPage: String(newPerPage)
    };

    if (currentSearch) {
      params.search = currentSearch;
    }

    setSearchParams(params);
  };

  const handleFilterSubmit = (
    values: IMileageDeviationFilter,
    formikHelpers: FormikHelpers<IMileageDeviationFilter>
  ) => {
    formikHelpers.setSubmitting(true);

    setActiveFilters({
      userUniqueId: values.userUniqueId,
      objectUniqueId: values.objectUniqueId
    });

    const currentParams = utils.getCommonParamsForApi();

    setSearchParams({
      page: "1",
      itemsPerPage: String(currentParams.itemsPerPage || 25)
    });

    formikHelpers.setSubmitting(false);
    setFilterModalOpen(false);
  };

  const handleClearFilters = () => {
    const currentParams = utils.getCommonParamsForApi();

    setActiveFilters({
      userUniqueId: "",
      objectUniqueId: ""
    });

    setSearchParams({
      page: "1",
      itemsPerPage: String(currentParams.itemsPerPage || 25)
    });
  };

  return (
    <Box>
      <Topbar
        theme={theme}
        title={t("drawerMenuLinks.mileageDeviations")}
        primaryButton={{
          variant: "text",
          text: sortAscending
            ? t("buttonsText.sortByDate") + " ↑"
            : t("buttonsText.sortByDate") + " ↓",
          onClick: handleSortByDate
        }}
        secondaryButton={{
          id: "mileage-deviation-filter",
          variant: "contained",
          text: `${t("buttonsText.filter")}`,
          onClick: () => setFilterModalOpen(true),
          sx: {
            backgroundColor: "#00B2A3",
            color: "#FFFFFF",
            marginLeft: "10px"
          }
        }}
      />
      <Box
        mx={4}
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          gap: 4,
          "@media (max-width: 768px)": {
            mx: 2
          },
          "@media (max-width: 480px)": {
            mx: 1
          }
        }}
      >
        <Box
          sx={{
            overflow: "hidden",
            width: "100%",
            display: "flex",
            flexDirection: "column"
          }}
        >
          <Table
            columns={tableColumns}
            data={mileageDeviations || []}
            progressPending={mileageDeviationLoading}
            paginationTotalRows={totalMileageDeviations ?? 0}
            paginationPerPage={parseInt(
              searchParams.get("itemsPerPage") || "10",
              10
            )}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            sortServer
            expandableRows
            expandOnRowClicked
            expandableRowsComponent={MileageDeviationCommentCard}
            noDataComponent={
              !mileageDeviationLoading &&
              (!mileageDeviations || mileageDeviations.length === 0) ? (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    padding: "20px",
                    borderRadius: "8px",
                    textAlign: "center",
                    width: "100%"
                  }}
                >
                  <Box
                    sx={{
                      width: "200px",
                      height: "200px"
                    }}
                  >
                    <NoDataIcon />
                  </Box>
                  <Typography
                    sx={{
                      color: "#323947",
                      fontWeight: 500,
                      fontSize: "16px",
                      marginTop: "24px",
                      textAlign: "center"
                    }}
                  >
                    {t("common.noDataAvailableYet")}
                  </Typography>
                  <Typography
                    sx={{
                      color: "#646D7B",
                      fontSize: "14px",
                      marginTop: "8px",
                      textAlign: "center"
                    }}
                  >
                    {t("common.mileageDeviationDescription")}
                  </Typography>
                </Box>
              ) : null
            }
            sx={{
              display: "flex",
              flexDirection: "column",
              "& .rdt_TableWrapper": {
                overflowX: "auto",
                minWidth: "100%",
                "&::-webkit-scrollbar": {
                  width: "8px",
                  height: "8px"
                },
                "&::-webkit-scrollbar-track": {
                  background: "#f1f1f1",
                  borderRadius: "4px"
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#c1c1c1",
                  borderRadius: "4px",
                  "&:hover": {
                    background: "#a8a8a8"
                  }
                },
                "&::-webkit-scrollbar-corner": {
                  background: "#f1f1f1"
                }
              },
              "& .rdt_Table": {
                border: "1px solid #D5D9E8",
                borderRadius: "10px",
                minWidth: "1600px",
                width: "100%",
                overflow: "hidden",
                "@media (max-width: 768px)": {
                  minWidth: "1200px"
                },
                "@media (max-width: 480px)": {
                  minWidth: "800px"
                }
              },
              "& .rdt_TableHead": {
                position: "sticky",
                top: 0,
                zIndex: 2,
                backgroundColor: "#fff"
              },
              "& .rdt_TableHeadRow": {
                backgroundColor: "#fff",
                borderBottom: "1px solid #e0e0e0",
                minHeight: "52px !important",
                "& > *:first-of-type": {
                  borderTopLeftRadius: "10px"
                },
                "& > *:last-child": {
                  borderTopRightRadius: "10px"
                }
              },
              "& .rdt_ExpanderButton": {
                minWidth: "48px",
                width: "48px",
                height: "48px",
                display: "flex !important",
                alignItems: "center",
                justifyContent: "center"
              },
              "& .rdt_TableRow[aria-disabled='true'] .rdt_ExpanderButton": {
                "& > *": {
                  opacity: "0"
                },
                backgroundColor: "transparent",
                cursor: "default"
              },
              "& .rdt_TableRow:not([aria-disabled='true']) .rdt_ExpanderButton":
                {
                  "& > *": {
                    opacity: "1"
                  },
                  cursor: "pointer"
                },
              "& .rdt_TableBody": {
                minHeight: "auto",
                overflowY: "visible"
              },
              "& .rdt_Pagination": {
                borderTop: "1px solid #e0e0e0",
                borderBottomLeftRadius: "10px",
                borderBottomRightRadius: "10px"
              }
            }}
            translator={t}
            nestedRowPadding="0px"
          />
        </Box>
      </Box>
      <MileageDeviationFilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        onSubmit={handleFilterSubmit}
        theme={theme}
        initialValues={activeFilters}
        hasActiveFilters={
          !!(activeFilters.userUniqueId || activeFilters.objectUniqueId)
        }
      />
    </Box>
  );
};

export default MileageDeviations;
